import React, { useMemo } from 'react';
import type { CodeThemePreviewStyle } from '../utils/monacoThemes.ts';
import { JS_GLOBAL_BUILTINS, PYTHON_BUILTIN_FUNCTIONS } from '../utils/monacoCodeLanguages.ts';
import './css/CodeSyntaxPreview.css';

const PY_JS_KEYWORDS = new Set([
  'def', 'class', 'return', 'if', 'elif', 'else', 'for', 'while', 'in', 'import', 'from',
  'as', 'with', 'try', 'except', 'finally', 'raise', 'pass', 'break', 'continue', 'lambda',
  'yield', 'async', 'await', 'const', 'let', 'var', 'function', 'new', 'typeof', 'instanceof',
  'switch', 'case', 'default', 'throw', 'catch', 'null', 'undefined', 'true', 'false',
  'and', 'or', 'not', 'is', 'del', 'global', 'nonlocal', 'assert', 'match', 'case',
]);

type TokenType =
  | 'comment'
  | 'keyword'
  | 'predefined'
  | 'type'
  | 'string'
  | 'number'
  | 'variable'
  | 'text';

type Token = {
  type: TokenType;
  value: string;
};

function isPredefinedWord(word: string): boolean {
  return PYTHON_BUILTIN_FUNCTIONS.has(word) || JS_GLOBAL_BUILTINS.has(word);
}

function tokenizeCodeLine(line: string): Token[] {
  const tokens: Token[] = [];
  let index = 0;

  while (index < line.length) {
    const rest = line.slice(index);

    if (rest.startsWith('#') || rest.startsWith('//')) {
      tokens.push({ type: 'comment', value: rest });
      break;
    }

    if (line[index] === '"' || line[index] === "'") {
      const quote = line[index];
      let end = index + 1;
      while (end < line.length && line[end] !== quote) {
        end += 1;
      }
      if (end < line.length) {
        end += 1;
      }
      tokens.push({ type: 'string', value: line.slice(index, end) });
      index = end;
      continue;
    }

    const wordMatch = rest.match(/^([a-zA-Z_][a-zA-Z0-9_]*)/);
    if (wordMatch) {
      const word = wordMatch[1];
      const lower = word.toLowerCase();
      let type: TokenType = 'variable';
      if (isPredefinedWord(word)) {
        type = 'predefined';
      } else if (PY_JS_KEYWORDS.has(lower)) {
        type = 'keyword';
      } else if (/^[A-Z]/.test(word)) {
        type = 'type';
      }
      tokens.push({ type, value: word });
      index += word.length;
      continue;
    }

    const numberMatch = rest.match(/^\d+/);
    if (numberMatch) {
      tokens.push({ type: 'number', value: numberMatch[0] });
      index += numberMatch[0].length;
      continue;
    }

    tokens.push({ type: 'text', value: line[index] });
    index += 1;
  }

  return tokens;
}

function colorForToken(type: TokenType, style: CodeThemePreviewStyle): string {
  switch (type) {
    case 'keyword':
      return style.keyword;
    case 'predefined':
      return style.function;
    case 'type':
      return style.type;
    case 'string':
      return style.string;
    case 'comment':
      return style.comment;
    case 'number':
      return style.number;
    case 'variable':
      return style.variable;
    default:
      return style.foreground;
  }
}

export type CodeSyntaxPreviewProps = {
  code: string;
  style: CodeThemePreviewStyle;
};

export function CodeSyntaxPreview({ code, style }: CodeSyntaxPreviewProps) {
  const lines = useMemo(
    () =>
      code.split('\n').map((line) => {
        if (/^\s*(#|\/\/)/.test(line)) {
          return [{ type: 'comment' as const, value: line }];
        }
        return tokenizeCodeLine(line);
      }),
    [code],
  );

  return (
    <pre
      className="code-syntax-preview"
      style={{
        backgroundColor: style.background,
        color: style.foreground,
      }}
    >
      <code>
        {lines.map((tokens, lineIndex) => (
          <span key={lineIndex} className="code-syntax-preview__line">
            {tokens.map((token, tokenIndex) => (
              <span
                key={tokenIndex}
                style={{
                  color: colorForToken(token.type, style),
                  fontStyle: token.type === 'comment' ? 'italic' : undefined,
                  fontWeight: token.type === 'keyword' ? 600 : undefined,
                }}
              >
                {token.value}
              </span>
            ))}
          </span>
        ))}
      </code>
    </pre>
  );
}
